import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { portfolioData } from '../../data/portfolioData';

const Hero = ({ floatingShapes }) => {
  const [titleIndex, setTitleIndex] = useState(0);
  const { roles, name, subtitle, ctaText } = portfolioData.hero;

  useEffect(() => {
    const timer = setInterval(() => {
      setTitleIndex((prev) => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [roles.length]);

  return (
    <motion.section 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="py-20 border-b border-gray-200 dark:border-gray-800 relative overflow-hidden bg-white dark:bg-gray-900 transition-colors duration-300"
    >
      <div className="container mx-auto px-6 text-center">
        {/* Profile Image */}
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="mb-12 flex justify-center"
        >
          <img 
            src="/ja.png" 
            alt="Profile"
            className="w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full shadow-xl hover:shadow-blue-600/30 dark:hover:shadow-blue-400/20 transition-all duration-500 border border-gray-200 dark:border-gray-800"
          />
        </motion.div>

        {/* Animated floating shapes */}
        <div className="absolute inset-0 pointer-events-none">
          {floatingShapes.map((shape, i) => (
            <motion.div
              key={i}
              className="absolute border-2 border-blue-600/15 dark:border-blue-400/10 rounded-full"
              style={{
                width: `${shape.width}px`,
                height: `${shape.height}px`,
                top: `${shape.top}%`,
                left: `${shape.left}%`,
              }}
              animate={{
                y: [0, -60, 20, -40, 0],
                x: [0, 30, -20, 10, 0],
                rotate: [0, 180, 360],
                scale: [1, 1.1, 0.95, 1.08, 1],
              }}
              transition={{
                duration: shape.duration,
                delay: shape.delay,
                repeat: Infinity,
                ease: [0.37, 0, 0.63, 1],
              }}
            />
          ))}
        </div>

        <motion.h1 
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 relative min-h-[1.5em]"
        >
          <div className="flex justify-center flex-col sm:flex-row items-center gap-x-3">
            <span className="inline-block">{name}</span>
            <span className="hidden sm:inline-block text-gray-400 dark:text-gray-500 font-normal">|</span>
            <div className="relative h-12 sm:h-auto overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.span
                  key={titleIndex}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="inline-block text-blue-600"
                >
                  {roles[titleIndex]}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>
        </motion.h1>

        <motion.p 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          {subtitle}
        </motion.p>

        <motion.div
           initial={{ y: 20, opacity: 0 }}
           animate={{ y: 0, opacity: 1 }}
           transition={{ delay: 1.1, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <a 
            href="#projects"
            className="inline-flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-blue-600/25 active:scale-95 group"
          >
            {ctaText}
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              ↓
            </motion.span>
          </a>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Hero;
